import axios from 'axios';
import { auth } from '../firebaseAuth';
import { getAuthHeaders } from '../utils/authHeaders';
import { resolveFrontendApiBase } from '../utils/apiBase';
import type { M2MHandoffRecord } from './types';

export interface M2MContext { userId: string; bookId: string; apiBase?: string }
interface HandoffResponse { handoff?: M2MHandoffRecord; checkoutUrl?: string }

export const requireM2MIdentity = (userId: string) => {
  if (!userId || auth.currentUser?.uid !== userId) {
    throw new Error('Sign in to access your Deep Dive reports.');
  }
};

const handoffsPath = ({ apiBase, bookId }: M2MContext) =>
  `${resolveFrontendApiBase(apiBase)}/api/books/${encodeURIComponent(bookId)}/marketing/m2m/handoffs`;

async function request<T>(context: M2MContext, url: string, method: 'get' | 'post', data?: unknown, signal?: AbortSignal, html = false): Promise<T> {
  requireM2MIdentity(context.userId);
  const headers = await getAuthHeaders();
  requireM2MIdentity(context.userId);
  const config = { headers, signal, ...(html ? { responseType: 'text' as const } : {}) };
  const response = method === 'get'
    ? await axios.get<T>(url, config)
    : await axios.post<T>(url, data, config);
  requireM2MIdentity(context.userId);
  if (signal?.aborted) throw new Error('Request cancelled');
  return response.data;
}

export const listM2MHandoffs = (context: M2MContext, signal?: AbortSignal, page = 1) => {
  const params = new URLSearchParams({ userId: context.userId, page: String(page), pageSize: '100' });
  return request<{ items: M2MHandoffRecord[]; hasMore?: boolean }>(context, `${handoffsPath(context)}?${params}`, 'get', undefined, signal);
};

export const listM2MLibraryStatus = (userId: string, signal?: AbortSignal) =>
  request<{ books: Record<string, M2MHandoffRecord[]> }>({ userId, bookId: '' },
    `${resolveFrontendApiBase()}/api/marketing/m2m/library-status?${new URLSearchParams({ userId })}`, 'get', undefined, signal);

export const createM2MHandoff = (context: M2MContext, payload: Record<string, unknown>) =>
  request<HandoffResponse>(context, handoffsPath(context), 'post', { ...payload, userId: context.userId });

export const retryM2MHandoff = (context: M2MContext, handoffId: string) =>
  request<HandoffResponse>(context, `${handoffsPath(context)}/${encodeURIComponent(handoffId)}/retry`, 'post', { userId: context.userId });

export const readM2MReport = (context: M2MContext, handoffId: string, signal?: AbortSignal) =>
  request<string>(context, `${handoffsPath(context)}/${encodeURIComponent(handoffId)}/report`, 'get', undefined, signal, true);

export const m2mErrorMessage = (error: unknown, fallback: string): string => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have access to this book or report.';
  if (status === 404) return 'This book or report is no longer available.';
  return fallback;
};

export const safeExternalM2MUrl = (raw?: string | null): string | null => {
  try {
    const url = new URL(raw || '');
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
  } catch { return null; }
};
